import { db, CachedPdaZoneSet, CachedPdaZone, OfflineBreadcrumb } from './db';
import { mustDeferSend, sendTenantOf } from './client-id';
import { api } from '@/services/api';
import { networkService } from '@/services/network.service';
import type { BreadcrumbInput } from '@/types/pda-zone';

class PdaZoneOfflineService {
  /**
   * Fetch zone sets + zones from API and store in Dexie for offline use.
   */
  async cacheZonesForOffline(tenantId: string, incidentId?: string): Promise<{ zoneSets: number; zones: number }> {
    const result = { zoneSets: 0, zones: 0 };

    try {
      const params = incidentId ? `?incident_id=${incidentId}` : '';
      const setsResponse = await api.get<{ data: any[] }>(`/pda-zones/sets${params}`);
      const serverSets = setsResponse.data || [];

      for (const s of serverSets) {
        const cached: CachedPdaZoneSet = {
          id: s.id,
          tenant_id: tenantId,
          incident_id: s.incident_id,
          name: s.name,
          grid_cell_size_meters: s.grid_cell_size_meters,
          boundary: s.boundary,
          zone_count: s.zone_count,
          zones_complete: s.zones_complete,
          created_at: s.created_at,
          updated_at: s.updated_at,
        };
        await db.cachedPdaZoneSets.put(cached);
        result.zoneSets++;

        // Fetch zones for this set
        try {
          const zonesResponse = await api.get<{ data: any[] }>(`/pda-zones/sets/${s.id}/zones`);
          const serverZones = zonesResponse.data || [];
          for (const z of serverZones) {
            const cachedZone: CachedPdaZone = {
              id: z.id,
              tenant_id: tenantId,
              zone_set_id: z.zone_set_id,
              zone_number: z.zone_number,
              name: z.name,
              boundary: z.boundary,
              status: z.status,
              assigned_to: z.assigned_to,
              assigned_to_name: z.assigned_to_name,
              coverage_percent: z.coverage_percent,
              grid_cells_total: z.grid_cells_total,
              grid_cells_visited: z.grid_cells_visited,
              notes: z.notes,
              created_at: z.created_at,
              updated_at: z.updated_at,
            };
            await db.cachedPdaZones.put(cachedZone);
            result.zones++;
          }
        } catch (err) {
          console.warn(`[PdaZoneOffline] Failed to cache zones for set ${s.id}:`, err);
        }
      }
    } catch (error) {
      console.warn('[PdaZoneOffline] Failed to cache from server:', error);
    }

    return result;
  }

  /**
   * Sync a specific zone set + its zones for offline use.
   * Called when a push notification triggers zone sync.
   *
   * It no longer downloads basemap tiles for the zone boundary: that bulk
   * tile scraper was removed in P0-20 (nothing ever read the tiles).
   */
  async syncZoneSetForOffline(
    tenantId: string,
    zoneSetId: string,
    _incidentId?: string
  ): Promise<{ zones: number }> {
    const result = { zones: 0 };

    try {
      // 1. Fetch the zone set
      const setResponse = await api.get<{ data: CachedPdaZoneSet }>(`/pda-zones/sets/${zoneSetId}`);
      const zoneSet = setResponse.data;
      if (!zoneSet) return result;

      // Cache the zone set
      await db.cachedPdaZoneSets.put({
        id: zoneSet.id,
        tenant_id: tenantId,
        incident_id: zoneSet.incident_id,
        name: zoneSet.name,
        grid_cell_size_meters: zoneSet.grid_cell_size_meters,
        boundary: zoneSet.boundary,
        zone_count: zoneSet.zone_count,
        zones_complete: zoneSet.zones_complete,
        created_at: zoneSet.created_at,
        updated_at: zoneSet.updated_at,
      });

      // 2. Fetch and cache all zones in the set
      const zonesResponse = await api.get<{ data: CachedPdaZone[] }>(`/pda-zones/sets/${zoneSetId}/zones`);
      const zones = zonesResponse.data || [];

      for (const z of zones) {
        await db.cachedPdaZones.put({
          id: z.id,
          tenant_id: tenantId,
          zone_set_id: z.zone_set_id,
          zone_number: z.zone_number,
          name: z.name,
          boundary: z.boundary,
          status: z.status,
          assigned_to: z.assigned_to,
          assigned_to_name: z.assigned_to_name,
          coverage_percent: z.coverage_percent,
          grid_cells_total: z.grid_cells_total,
          grid_cells_visited: z.grid_cells_visited,
          notes: z.notes,
          created_at: z.created_at,
          updated_at: z.updated_at,
        });
        result.zones++;
      }
    } catch (error) {
      console.error('[PdaZoneOffline] Failed to sync zone set:', error);
    }

    return result;
  }

  async getCachedZoneSets(incidentId?: string): Promise<CachedPdaZoneSet[]> {
    if (incidentId) {
      return db.cachedPdaZoneSets.where('incident_id').equals(incidentId).toArray();
    }
    return db.cachedPdaZoneSets.toArray();
  }

  async getCachedZones(zoneSetId: string): Promise<CachedPdaZone[]> {
    return db.cachedPdaZones.where('zone_set_id').equals(zoneSetId).toArray();
  }

  async getCachedZone(zoneId: string): Promise<CachedPdaZone | undefined> {
    return db.cachedPdaZones.get(zoneId);
  }

  async saveBreadcrumbsOffline(zoneId: string, points: BreadcrumbInput[]): Promise<void> {
    const breadcrumbs: OfflineBreadcrumb[] = points.map((p) => ({
      id: crypto.randomUUID(),
      zone_id: zoneId,
      lat: p.lat,
      lng: p.lng,
      accuracy: p.accuracy,
      altitude: p.altitude,
      speed: p.speed,
      heading: p.heading,
      recorded_at: p.recorded_at,
      is_synced: false,
    }));

    await db.offlineBreadcrumbs.bulkAdd(breadcrumbs);
  }

  async syncBreadcrumbs(): Promise<{ synced: number; failed: number }> {
    const result = { synced: 0, failed: 0 };
    if (!networkService.isOnline()) return result;

    // Loose check — breadcrumbs are written is_synced:false (boolean); IndexedDB
    // can't index booleans, so an index .equals(0) missed them and GPS
    // breadcrumbs never synced (and showed 0 pending).
    const unsynced = await db.offlineBreadcrumbs
      .filter(b => !b.is_synced)
      .toArray();

    if (unsynced.length === 0) return result;

    // Never sent as someone else (S-06): another originator's row waits for them, or for the handoff.
    const sendable = unsynced.filter((b) => !mustDeferSend(b as never));

    // Group by zone AND tenant for batch posting: each batch is sent under its rows' own tenant.
    const byZone = new Map<string, OfflineBreadcrumb[]>();
    for (const b of sendable) {
      const key = `${sendTenantOf(b).tenantId}|${b.zone_id}`;
      const list = byZone.get(key) || [];
      list.push(b);
      byZone.set(key, list);
    }

    for (const breadcrumbs of byZone.values()) {
      const zoneId = breadcrumbs[0].zone_id;
      const BATCH_SIZE = 100;
      for (let i = 0; i < breadcrumbs.length; i += BATCH_SIZE) {
        const batch = breadcrumbs.slice(i, i + BATCH_SIZE);
        try {
          await api.post(`/pda-zones/zones/${zoneId}/breadcrumbs`, {
            points: batch.map((b) => ({
              lat: b.lat,
              lng: b.lng,
              accuracy: b.accuracy,
              altitude: b.altitude,
              speed: b.speed,
              heading: b.heading,
              recorded_at: b.recorded_at,
            })),
          }, sendTenantOf(batch[0]));

          const ids = batch.map((b) => b.id);
          await db.offlineBreadcrumbs.where('id').anyOf(ids).modify({ is_synced: true });
          result.synced += batch.length;
        } catch (err) {
          console.error(`[PdaZoneOffline] Failed to sync breadcrumbs for zone ${zoneId}:`, err);
          result.failed += batch.length;
        }
      }
    }

    // Clean up synced breadcrumbs (loose: they're marked is_synced:true boolean,
    // which an index .equals(1) can't match).
    await db.offlineBreadcrumbs.filter(b => !!b.is_synced).delete();

    return result;
  }

  async getOfflineBreadcrumbs(zoneId: string): Promise<OfflineBreadcrumb[]> {
    return db.offlineBreadcrumbs
      .where('zone_id')
      .equals(zoneId)
      .sortBy('recorded_at');
  }

  async getPendingBreadcrumbCount(): Promise<number> {
    return db.offlineBreadcrumbs
      .filter(b => !b.is_synced)
      .count();
  }
}

export const pdaZoneOfflineService = new PdaZoneOfflineService();
